/**
 * `play-api`'s doors (D156) — the request and response bodies of the play
 * Worker, as Zod, so the Worker validates what arrives and the remote host
 * validates what comes back.
 *
 * @remarks
 * Three doors and an export:
 * - `POST /sessions` — {@link openSessionRequestSchema} in, {@link sessionEnvelopeSchema} out (201).
 * - `POST /sessions/:id/turns` — {@link submitTurnRequestSchema} in, {@link turnEnvelopeSchema} out.
 * - `GET /sessions/:id` — {@link sessionEnvelopeSchema} out: the resume.
 * - `GET /sessions/:id/export` — `text/plain`, the transcript export (R20).
 * - `POST /sessions/:id/port` — {@link portChoiceRequestSchema} in, {@link sessionEnvelopeSchema} out: the player's
 *   answer to a {@link portReportSchema} the resume carried (D163).
 *
 * The session id travels in the path; the player's claim to it travels in
 * an `httpOnly` cookie the Worker set when it opened the session. A caller
 * with the id and not the cookie is answered `404`, the same as a session
 * that never existed — the id is not a secret, the cookie is.
 *
 * @packageDocumentation
 */

import { gameIdSchema, randomSeedSchema, recordLayersSchema } from '@orpheus/content-schema';
import { z } from 'zod';

import { gameDescriptionSchema } from './game-description.js';
import { sessionViewSchema, turnOutcomeSchema, turnVerdictSchema } from './session-view.js';

/** The longest input the door accepts — a sentence, not a document; the engine's own limits sit below this. */
export const INPUT_LENGTH_LIMIT = 500;

/**
 * Open a session on a game; a seed only when the caller has one (the
 * marketing link, a bug report); and (D241) the Record this DEVICE holds,
 * when it holds one — read at genesis for a player who is not signed in.
 * A signed-in player's Record is the account's, read by the Worker itself;
 * whatever the device sends then is not read.
 */
export const openSessionRequestSchema = z
	.strictObject({
		gameId: gameIdSchema,
		record: recordLayersSchema.optional(),
		seed: randomSeedSchema.optional(),
	})
	.readonly();

export type OpenSessionRequest = z.infer<typeof openSessionRequestSchema>;

/** One turn: the player's words, as typed. */
export const submitTurnRequestSchema = z
	.strictObject({
		input: z.string().max(INPUT_LENGTH_LIMIT),
	})
	.readonly();

export type SubmitTurnRequest = z.infer<typeof submitTurnRequestSchema>;

/**
 * Which part of the player's footprint a turn lands differently on (D163):
 * the verdict on their words, the room they end in, what they carry, whether
 * the game is asking them something, or which endings have been selected.
 * Never prose — a rewrite ports in silence; only what the player would feel.
 */
export const portDifferenceSchema = z.enum(['carried', 'ending', 'question', 'room', 'verdict']);

export type PortDifference = z.infer<typeof portDifferenceSchema>;

/**
 * The replay's report (D163, platform.md §6): a newer version of the game
 * is published, the session's commands were replayed against it, and the
 * first one that lands differently is named — so the shell can tell the
 * player what changed in their own words and let them update or stay.
 */
export const portReportSchema = z
	.strictObject({
		/**
		 * D257: why the session replays differently. Absent, a newer version of
		 * the GAME (D163) — updating moves to it, staying keeps this one. `engine`,
		 * the same version replayed on a changed engine: the game as it was
		 * played is gone, so updating rewinds to before the first command that
		 * changed, and staying carries on as it now plays.
		 */
		cause: z.literal('engine').optional(),
		/** The first command the new version answers differently. */
		divergedAt: z
			.strictObject({
				command: z.string(),
				differences: z.array(portDifferenceSchema).min(1).readonly(),
				/** The verdict on the new version. */
				now: turnVerdictSchema,
				/** Its place among the session's commands, counted from one — every command, not only the turns that consumed one. */
				position: z.int().positive(),
				/** The verdict the player saw. */
				was: turnVerdictSchema,
			})
			.readonly(),
		/** How many commands land as they did: updating continues from after them, and the rest are lost. */
		keptCommands: z.int().nonnegative(),
		/** The version the session would move to — the hash of its bundle. */
		toBundleHash: z.string().regex(/^[0-9a-f]{64}$/u),
		/** How many commands the session has, so the shell can say what updating would cost. */
		totalCommands: z.int().positive(),
	})
	.readonly();

export type PortReport = z.infer<typeof portReportSchema>;

/** The player's answer to a pending port: move to the new version from the last matching turn, or stay on this one. */
export const portChoiceRequestSchema = z
	.strictObject({
		choice: z.enum(['stay', 'update']),
	})
	.readonly();

export type PortChoiceRequest = z.infer<typeof portChoiceRequestSchema>;

/** A session as the door hands it over: who it is, what the game is, and the view right now. */
export const sessionEnvelopeSchema = z
	.strictObject({
		game: gameDescriptionSchema,
		/** A newer version the session could not follow silently (D163) — present until the player answers it. */
		pendingPort: portReportSchema.optional(),
		/** The seed the session runs on — so a player can quote it in a bug report, exactly as `?seed=` would. */
		seed: randomSeedSchema,
		sessionId: z.string().min(1),
		view: sessionViewSchema,
	})
	.readonly();

export type SessionEnvelope = z.infer<typeof sessionEnvelopeSchema>;

/** One turn's answer. */
export const turnEnvelopeSchema = z
	.strictObject({
		outcome: turnOutcomeSchema,
		sessionId: z.string().min(1),
	})
	.readonly();

export type TurnEnvelope = z.infer<typeof turnEnvelopeSchema>;

/**
 * What `GET /hosts/:hostname` answers (D162): the game this hostname serves,
 * described, and the hash of its current bundle — which a client-mode game
 * fetches by that hash from `GET /bundles/:gameId/:hash`, immutable and
 * cacheable forever, and a server-mode game never fetches at all.
 */
export const hostEnvelopeSchema = z
	.strictObject({
		/** Where the game's assets are served from — the browser's loader joins it onto every `src`, as the edge's does. */
		assetBaseUrl: z.string().min(1).optional(),
		bundleHash: z.string().regex(/^[0-9a-f]{64}$/u),
		game: gameDescriptionSchema,
		gameId: gameIdSchema,
	})
	.readonly();

export type HostEnvelope = z.infer<typeof hostEnvelopeSchema>;

/**
 * What `GET /hosts/:hostname` answers at a root that serves no game (D198):
 * the landing page on the same origin the browser is to be sent to — a
 * demo hostname's map of what it holds under its paths — and (D214) the
 * hostname's own 404 page, when its record names one.
 */
export const hostLandingEnvelopeSchema = z
	.strictObject({
		landing: z.string().regex(/^\/[^\s]*$/u),
		notFound: z.string().regex(/^\/[^\s]*$/u).optional(),
	})
	.readonly();

export type HostLandingEnvelope = z.infer<typeof hostLandingEnvelopeSchema>;

/** Either answer of the hosts door: a game to play here, or a page to go to. */
export const hostAnswerSchema = z.union([hostEnvelopeSchema, hostLandingEnvelopeSchema]);

export type HostAnswer = z.infer<typeof hostAnswerSchema>;

/** Why a door refused, by name — never a stack, never the world. */
export const playApiErrorSchema = z
	.strictObject({
		error: z.enum([
			'game_not_found',
			'input_too_long',
			'not_a_session_request',
			'not_a_turn_request',
			'not_entitled',
			'not_found',
			/** A server game's bundle never leaves the edge (D148 §7) — asked for anyway, this is the answer. */
			'bundle_not_public',
			'host_not_found',
			'not_json',
			/** A port choice arrived, and no port is pending on the session (D163). */
			'no_port_pending',
			'not_a_port_choice',
			/** Too many, too fast — from this address or on this session; `Retry-After` says when (D161). */
			'rate_limited',
			/** D242: a "Forget the Record" whose body is not one game's id. */
			'not_a_record_request',
			/** D242: the store did not forget a signed-in player's Record — nothing is claimed; asking again is safe. */
			'record_unavailable',
			'session_not_found',
		]),
	})
	.readonly();

export type PlayApiError = z.infer<typeof playApiErrorSchema>;
